/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Divider, SelectChangeEvent, Stack } from '@mui/material';
import Checkbox from '../../../../components/Checkbox';
import Typography from '../../../../components/Typography';
import Input from '../../../../components/Input';
import DropdownList, { DropdownItem } from '../../../../components/Dropdownlist';
import { SalesAgreementPromoProductSelectionVariant } from './types';

export const feedRates: DropdownItem[] = [
  { label: '0.5kg per head/day', value: '0.5' },
  { label: '1.0kg per head/day', value: '1' },
  { label: '1.5kg per head/day', value: '1.5' },
  { label: '2.0kg per head/day', value: '2' },
  { label: '2.5kg per head/day', value: '2.5' },
  { label: '3.0kg per head/day', value: '3' },
  { label: '3.5kg per head/day', value: '3.5' },
  { label: '4.0kg per head/day', value: '4' },
  { label: '4.5kg per head/day', value: '4.5' },
  { label: '5.0kg per head/day', value: '5' },
  { label: '5.5kg per head/day', value: '5.5' },
  { label: '6.0kg per head/day', value: '6' },
  { label: '6.5kg per head/day', value: '6.5' },
  { label: '7.0kg per head/day', value: '7' },
  { label: '7.5kg per head/day', value: '7.5' },
  { label: '8.0kg per head/day', value: '8' },
  { label: '8.5kg per head/day', value: '8.5' },
  { label: '9.0kg per head/day', value: '9' },
  { label: '9.5kg per head/day', value: '9.5' },
  { label: '10.0kg per head/day', value: '10' },
];

export const FeedRateDisplay = ({
  feedRate,
  additives,
}: {
  feedRate?: string;
  additives?: string;
}) => {
  return (
    <Stack direction='column' gap={1}>
      <Divider sx={{ mt: 2 }} />
      <Typography variant='body2' fontWeight={600}>
        Planned feed rate
      </Typography>
      <Typography variant='body2' fontWeight={400}>
        {feedRate}
      </Typography>

      <Typography variant='body2' fontWeight={600}>
        Potential Additives
      </Typography>
      <Typography variant='body2' fontWeight={400}>
        {additives}
      </Typography>
    </Stack>
  );
};

const AdditivesOption = ({
  product,
  editable,
  setEnable,
  setFeedRate,
  setAdditives,
}: {
  setEnable: (checked: boolean) => void;
  setFeedRate: (value: string) => void;
  setAdditives: (value: string) => void;
  product: SalesAgreementPromoProductSelectionVariant;
  editable?: boolean;
}) => {
  if (!editable)
    return <FeedRateDisplay feedRate={product.feedRate?.label} additives={product.additives} />;

  return (
    <Stack direction='column' gap={1}>
      <Divider sx={{ mt: 2 }} />
      <Checkbox
        label={
          <Typography variant='body2' fontWeight={400}>
            Additives and feed rates
          </Typography>
        }
        checked={product.feedRate !== undefined}
        onChange={(_, checked) => setEnable(checked)}
      />
      {product.feedRate && (
        <>
          <DropdownList
            items={feedRates}
            label='Planned feed rate'
            sx={{ maxHeight: '300px' }}
            MenuProps={{
              sx: {
                maxHeight: '300px !important',
                boxShadow: '0px -1px 1px 0px rgba(0, 0, 0, 0.1)',
              },
            }}
            value={product.feedRate?.value}
            onChange={(event: SelectChangeEvent<string>) => {
              setFeedRate(event.target.value);
            }}
            testId='TODO:test'
          />

          <Input
            label='Potential Additives'
            value={product.additives}
            onChange={(e) => setAdditives(e.target.value)}
            type='text'
            testId='TODO:test'
            multiline
            minRows={3}
            placeholder='Enter additive details here...'
          />
        </>
      )}
    </Stack>
  );
};

export default AdditivesOption;
